import { Badge, Checkbox, Text } from "@fluentui/react-components";
import * as React from "react";
import { $borderForeground1 } from "../../../cssjs/colors";
import { toSentenceCase } from "../../../utils/common/CommonHelperFunctions";
import {
  getNotificationCategoryIcon,
  getNotificationCategoryLabel,
  getSeverityColor,
} from "../../../utils/notification/NotificationPanelHelperFunctions";
import { NotificationItem } from "../types";
import { useCardStyles } from "./styles";

export interface NotificationCardProps {
  notification: NotificationItem;
  selected: boolean;
  onToggleSelect: (id: string, checked: boolean) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  selected,
  onToggleSelect,
}) => {
  const styles = useCardStyles();

  const {
    id,
    title,
    body,
    invalid,
    severity,
    category,
    timestamp,
    updatedTimestamp,
    start,
    end,
  } = notification;
  const categoryIcon = getNotificationCategoryIcon(category);
  const categoryLabel = getNotificationCategoryLabel(category);
  const displayedTimestamp = invalid ? updatedTimestamp : timestamp;

  return (
    <article className={styles.card}>
      <Checkbox
        className={styles.cardCheckbox}
        checked={selected}
        onChange={(_, data) => onToggleSelect(id, !!data.checked)}
        aria-label={`Select notification: ${title}`}
      />

      <div className={styles.cardMain}>
        <div className={styles.cardTitleRow}>
          <span className={styles.cardTitleWithIcon}>
            {categoryIcon ? (
              <span className={styles.cardTitleIconContainer}>
                <img
                  src={categoryIcon}
                  alt={`${category} icon`}
                  aria-hidden="true"
                  className={styles.cardTitleIcon}
                />
              </span>
            ) : null}
            <span className={styles.cardTitle}>{title}</span>
          </span>
          <span className={styles.cardTimestamp}>{displayedTimestamp}</span>
        </div>

        <div className={styles.cardTagsContainer}>
          <div className={styles.cardTags}>
            <Badge
              appearance="outline"
              shape="circular"
              style={{ padding: "12px", color: $borderForeground1 }}
            >
              {categoryLabel}
            </Badge>
            <Badge
              appearance="filled"
              shape="circular"
              color={getSeverityColor(severity)}
              style={{ padding: "12px" }}
            >
              {toSentenceCase(severity)}
            </Badge>
          </div>

          <Text className={styles.cardImpact}>
            <span className={styles.cardImpactLabel}>Impact: </span>
            {start}
            {end ? ` - ${end}` : null}
          </Text>
        </div>

        <Text className={styles.cardBody}>{body}</Text>
      </div>
    </article>
  );
};

export default NotificationCard;
